import { useState, useEffect } from "react";
import { calcMonthly } from "@/lib/providers/index";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import SandboxBadge from "@/components/SandboxBadge";

const SUPABASE_URL = "https://mfhdaiymdtwhsnaximji.supabase.co";
const PUB_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im1maGRhaXltZHR3aHNuYXhpbWppIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTYzMTE1MDUsImV4cCI6MjA3MTg4NzUwNX0.E-Mrze3hhKxO5hTf-j4iYa1v4AIdIs8zicdUjNatcRA";

type Stay = { id: string; name: string; city: string; priceAed: number; nights: number };

const MOCK_STAYS: Stay[] = [
  { id: "s1", name: "Marina View Hotel", city: "Dubai", priceAed: 1800, nights: 3 },
  { id: "s2", name: "Beach Resort Deluxe", city: "Abu Dhabi", priceAed: 3200, nights: 4 },
  { id: "s3", name: "Old Town Boutique", city: "Athens", priceAed: 2200, nights: 3 },
  { id: "s4", name: "London Central Hotel", city: "London", priceAed: 2800, nights: 4 },
  { id: "s5", name: "Champs-Élysées Suite", city: "Paris", priceAed: 3600, nights: 5 },
  { id: "s6", name: "Tokyo Business Hotel", city: "Tokyo", priceAed: 2400, nights: 3 },
  { id: "s7", name: "Riverside Bangkok Hotel", city: "Bangkok", priceAed: 1600, nights: 3 },
  { id: "s8", name: "Marina Bay Suites", city: "Singapore", priceAed: 2600, nights: 4 },
  { id: "s9", name: "Colosseum View Hotel", city: "Rome", priceAed: 2400, nights: 4 },
  { id: "s10", name: "Bosphorus View Hotel", city: "Istanbul", priceAed: 1400, nights: 3 },
  { id: "s11", name: "Santorini Luxury Suite", city: "Santorini", priceAed: 3400, nights: 5 },
  { id: "s12", name: "Riviera Resort", city: "Nice", priceAed: 3000, nights: 4 },
];

async function createLead(providerCode: "tabby"|"tamara"|"cashew", amountAED: number, name: string, email: string, phone?: string, travelListingId?: string) {
  if (!travelListingId) {
    throw new Error("Travel listing not configured");
  }

  const res = await fetch(`${SUPABASE_URL}/functions/v1/lead-create`, {
    method: "POST",
    headers: { "Authorization": `Bearer ${PUB_KEY}`, "Content-Type": "application/json" },
    body: JSON.stringify({
      listingId: travelListingId,
      providerCode,
      name, email, phone,
      utm: { source: "travel_stays_demo" }
    })
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json() as Promise<{ id: string }>;
}

export default function Stays() {
  const [providerCode, setProviderCode] = useState<"tabby"|"tamara"|"cashew">("tabby");
  const [travelListingId, setTravelListingId] = useState<string>("");

  // Get the travel listing ID on component mount
  useEffect(() => {
    const fetchTravelListing = async () => {
      const { data, error } = await supabase
        .from('listings')
        .select('id')
        .eq('title', 'Flight Booking (Demo)')
        .eq('category', 'travel')
        .single();
      
      if (data && !error) {
        setTravelListingId(data.id);
      } else {
        console.error('Failed to fetch travel listing:', error);
        toast.error('Failed to load travel configuration');
      }
    };

    fetchTravelListing();
  }, []);

  const onCheckout = async (stay: Stay) => {
    const name = (document.getElementById("stayName") as HTMLInputElement)?.value || "Demo User";
    const email = (document.getElementById("stayEmail") as HTMLInputElement)?.value || "demo@example.com";
    const phone = (document.getElementById("stayPhone") as HTMLInputElement)?.value || "";

    try {
      await createLead(providerCode, stay.priceAed, name, email, phone, travelListingId);
      
      // Redirect to demo confirmation page
      const demoConfirmUrl = `/booking/confirmed?amount=${encodeURIComponent(stay.priceAed)}&currency=AED&provider=${encodeURIComponent(providerCode)}&ref=${encodeURIComponent('BNPL-' + Date.now())}`;
      window.location.href = demoConfirmUrl;
    } catch (e: any) {
      toast.error(e.message ?? "Failed to create lead");
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <h2 className="text-2xl font-semibold mb-3 text-foreground">Browse stays</h2>
      <SandboxBadge />
      <div className="flex items-center gap-3 mb-3">
        <select 
          className="border border-border rounded-lg px-3 py-2 bg-background text-foreground" 
          value={providerCode} 
          onChange={e=>setProviderCode(e.target.value as any)}
        >
          <option value="tabby">Tabby</option>
          <option value="tamara">Tamara</option>
          <option value="cashew">Cashew</option>
        </select>
        <input 
          id="stayName" 
          placeholder="Your name" 
          className="border border-border rounded-lg px-3 py-2 bg-background text-foreground"
        />
        <input 
          id="stayEmail" 
          placeholder="Email" 
          className="border border-border rounded-lg px-3 py-2 bg-background text-foreground"
        />
        <input 
          id="stayPhone" 
          placeholder="Phone (optional)" 
          className="border border-border rounded-lg px-3 py-2 bg-background text-foreground"
        />
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {MOCK_STAYS.map((s) => {
          const perMonth = calcMonthly(s.priceAed, 12);
          return (
            <div key={s.id} className="border border-border rounded-2xl p-4 bg-card">
              <div className="text-sm text-muted-foreground">{s.city}</div>
              <div className="text-lg font-semibold text-foreground">{s.name}</div>
              <div className="mt-1 text-foreground">AED {s.priceAed.toLocaleString()} · {s.nights} nights</div>

              <div className="mt-2 text-sm">
                <div className="text-foreground">
                  Example: <strong>AED {perMonth}/mo</strong> over 12 months via <strong>{providerCode}</strong>
                </div>
                <div className="text-muted-foreground">
                  Subject to provider approval. Flexaura is a marketplace and does not lend.
                </div>
              </div>

              <div className="mt-3">
                <button 
                  onClick={()=>onCheckout(s)} 
                  className="rounded-lg bg-primary text-primary-foreground px-4 py-2 hover:bg-primary/90"
                >
                  Pay in installments
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}